/**
 * Validation Hooks
 *
 * React Query hooks for validation operations.
 * Provides mutation hooks for validating submissions, employees, and NI numbers.
 */

import { useMutation } from '@tanstack/react-query';
import { useState, useCallback, useRef, useEffect } from 'react';
import { validationApi } from '@/services/api/validationApi';
import type {
  SubmissionValidationResult,
  ValidationSummaryResult,
  EmployeeValidationResult,
  NIValidationResult,
  ValidateSubmissionRequest,
  ValidateNIRequest,
  ValidateEmployeeRequest,
  EmployeeValidationData,
  AcknowledgedWarnings,
} from '@/types/validation';

// =============================================================================
// QUERY KEYS
// =============================================================================

export const validationKeys = {
  all: ['validation'] as const,
  submission: (id: string) =>
    [...validationKeys.all, 'submission', id] as const,
  ni: (niNumber: string) => [...validationKeys.all, 'ni', niNumber] as const,
};

// =============================================================================
// SUBMISSION VALIDATION
// =============================================================================

/**
 * Hook for validating a full submission.
 *
 * @example
 * ```tsx
 * const { mutate: validate, data, isPending } = useValidateSubmission();
 *
 * validate({
 *   employer_ni: '123456789',
 *   contribution_period: '0126',
 *   employees: [...],
 * });
 * ```
 */
export function useValidateSubmission() {
  return useMutation({
    mutationFn: (data: ValidateSubmissionRequest) =>
      validationApi.validateSubmission(data),
  });
}

/**
 * Hook for validating a submission and getting summary only.
 * Lighter weight than full validation.
 */
export function useValidateSubmissionSummary() {
  return useMutation({
    mutationFn: (data: ValidateSubmissionRequest) =>
      validationApi.validateSubmissionSummary(data),
  });
}

// =============================================================================
// NI NUMBER VALIDATION
// =============================================================================

/**
 * Hook for validating a single NI number.
 * Returns validation result with suggestions for typos.
 *
 * @example
 * ```tsx
 * const { mutate: validateNI, data } = useValidateNI();
 * validateNI({ ni_number: '70078874' });
 *
 * if (data?.suggestions.length) {
 *   console.log('Did you mean:', data.suggestions);
 * }
 * ```
 */
export function useValidateNI() {
  return useMutation({
    mutationFn: (data: ValidateNIRequest) => validationApi.validateNI(data),
  });
}

// =============================================================================
// EMPLOYEE VALIDATION
// =============================================================================

/**
 * Hook for validating a single employee.
 * Useful for real-time form validation.
 */
export function useValidateEmployee() {
  return useMutation({
    mutationFn: (data: ValidateEmployeeRequest) =>
      validationApi.validateEmployee(data),
  });
}

/**
 * Hook for batch validating multiple employees.
 */
export function useValidateBatch() {
  return useMutation({
    mutationFn: (employees: EmployeeValidationData[]) =>
      validationApi.validateBatch(employees),
  });
}

// =============================================================================
// REAL-TIME VALIDATION WITH DEBOUNCE
// =============================================================================

interface UseRealTimeValidationOptions {
  /** Debounce delay in milliseconds (default: 300) */
  debounceMs?: number;
  /** Context for validation */
  context?: {
    employer_ni?: string;
    contribution_period?: string;
    format_type?: 'HOSPITALITY' | 'NON_HOSPITALITY';
    pay_indicator?: 'W' | 'B' | 'S' | 'M';
  };
}

/**
 * Hook for real-time validation with debouncing.
 * Automatically validates employee data as user types.
 *
 * @example
 * ```tsx
 * const { validate, result, isValidating, clear } = useRealTimeValidation({
 *   debounceMs: 500,
 *   context: { employer_ni: '123456789', contribution_period: '0126' },
 * });
 *
 * // Call validate whenever form data changes
 * useEffect(() => {
 *   validate(employeeData);
 * }, [employeeData]);
 * ```
 */
export function useRealTimeValidation(
  options: UseRealTimeValidationOptions = {}
) {
  const { debounceMs = 300, context } = options;

  const [result, setResult] = useState<EmployeeValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const clear = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setResult(null);
    setError(null);
    setIsValidating(false);
  }, []);

  const validate = useCallback(
    (employee: EmployeeValidationData) => {
      // Clear previous timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      setIsValidating(true);
      setError(null);

      timeoutRef.current = setTimeout(async () => {
        abortControllerRef.current = new AbortController();

        try {
          const validationResult = await validationApi.validateEmployee({
            ...context,
            employee,
          });
          setResult(validationResult);
          setError(null);
        } catch (err) {
          if (err instanceof Error && err.name !== 'AbortError') {
            setError(err);
          }
        } finally {
          setIsValidating(false);
        }
      }, debounceMs);
    },
    [context, debounceMs]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    validate,
    result,
    isValidating,
    error,
    clear,
  };
}

// =============================================================================
// REAL-TIME NI VALIDATION
// =============================================================================

interface UseRealTimeNIValidationOptions {
  /** Debounce delay in milliseconds (default: 300) */
  debounceMs?: number;
}

/**
 * Hook for real-time NI number validation with debouncing.
 *
 * @example
 * ```tsx
 * const { validate, result, isValidating } = useRealTimeNIValidation();
 *
 * <input
 *   onChange={(e) => validate(e.target.value)}
 * />
 * ```
 */
export function useRealTimeNIValidation(
  options: UseRealTimeNIValidationOptions = {}
) {
  const { debounceMs = 300 } = options;

  const [result, setResult] = useState<NIValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setResult(null);
    setError(null);
    setIsValidating(false);
  }, []);

  const validate = useCallback(
    (niNumber: string) => {
      // Clear previous timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Skip validation if NI is empty or too short
      if (!niNumber || niNumber.length < 8) {
        setResult(null);
        setIsValidating(false);
        return;
      }

      setIsValidating(true);
      setError(null);

      timeoutRef.current = setTimeout(async () => {
        try {
          const validationResult = await validationApi.validateNI({
            ni_number: niNumber,
          });
          setResult(validationResult);
          setError(null);
        } catch (err) {
          if (err instanceof Error) {
            setError(err);
          }
        } finally {
          setIsValidating(false);
        }
      }, debounceMs);
    },
    [debounceMs]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    validate,
    result,
    isValidating,
    error,
    clear,
  };
}

// =============================================================================
// WARNING ACKNOWLEDGMENT
// =============================================================================

/**
 * Hook for tracking acknowledged warnings.
 * Persists acknowledgments across re-validations.
 *
 * @example
 * ```tsx
 * const {
 *   isAcknowledged,
 *   acknowledge,
 *   acknowledgeAll,
 *   reset,
 *   allAcknowledged,
 * } = useAcknowledgedWarnings(validationResult);
 *
 * // Check if a specific warning is acknowledged
 * if (!isAcknowledged('70078874', 'BUSINESS_WAGES_EXCEED_CEILING')) {
 *   // Show acknowledge button
 * }
 * ```
 */
export function useAcknowledgedWarnings(
  validationResult: SubmissionValidationResult | null
) {
  const [acknowledged, setAcknowledged] = useState<AcknowledgedWarnings>({
    acknowledgedCodes: new Set(),
    acknowledgedEmployees: new Set(),
  });

  /**
   * Check if a specific warning is acknowledged.
   */
  const isAcknowledged = useCallback(
    (employeeNi: string, code: string): boolean => {
      const key = `${employeeNi}:${code}`;
      return acknowledged.acknowledgedCodes.has(key);
    },
    [acknowledged]
  );

  /**
   * Acknowledge a specific warning.
   */
  const acknowledge = useCallback((employeeNi: string, code: string) => {
    setAcknowledged(prev => {
      const newCodes = new Set(prev.acknowledgedCodes);
      newCodes.add(`${employeeNi}:${code}`);
      return {
        ...prev,
        acknowledgedCodes: newCodes,
      };
    });
  }, []);

  /**
   * Acknowledge all warnings for an employee.
   */
  const acknowledgeAllForEmployee = useCallback(
    (employeeNi: string) => {
      if (!validationResult) return;

      const employeeResult = validationResult.employee_results.find(
        r => r.employee_ni === employeeNi
      );
      if (!employeeResult) return;

      setAcknowledged(prev => {
        const newCodes = new Set(prev.acknowledgedCodes);
        const newEmployees = new Set(prev.acknowledgedEmployees);

        employeeResult.issues
          .filter(i => i.severity === 'WARNING')
          .forEach(issue => {
            newCodes.add(`${employeeNi}:${issue.code}`);
          });

        newEmployees.add(employeeNi);

        return {
          acknowledgedCodes: newCodes,
          acknowledgedEmployees: newEmployees,
        };
      });
    },
    [validationResult]
  );

  /**
   * Acknowledge all warnings in the submission.
   */
  const acknowledgeAll = useCallback(() => {
    if (!validationResult) return;

    setAcknowledged(() => {
      const newCodes = new Set<string>();
      const newEmployees = new Set<string>();

      validationResult.employee_results.forEach(emp => {
        emp.issues
          .filter(i => i.severity === 'WARNING')
          .forEach(issue => {
            newCodes.add(`${emp.employee_ni}:${issue.code}`);
          });
        if (emp.has_warnings) {
          newEmployees.add(emp.employee_ni);
        }
      });

      return {
        acknowledgedCodes: newCodes,
        acknowledgedEmployees: newEmployees,
      };
    });
  }, [validationResult]);

  /**
   * Reset all acknowledgments.
   */
  const reset = useCallback(() => {
    setAcknowledged({
      acknowledgedCodes: new Set(),
      acknowledgedEmployees: new Set(),
    });
  }, []);

  /**
   * Check if all warnings have been acknowledged.
   */
  const allAcknowledged = useCallback((): boolean => {
    if (!validationResult) return true;
    if (!validationResult.requires_acknowledgment) return true;

    for (const emp of validationResult.employee_results) {
      for (const issue of emp.issues) {
        if (issue.severity === 'WARNING') {
          if (!isAcknowledged(emp.employee_ni, issue.code)) {
            return false;
          }
        }
      }
    }

    return true;
  }, [validationResult, isAcknowledged]);

  /**
   * Get count of unacknowledged warnings.
   */
  const unacknowledgedCount = useCallback((): number => {
    if (!validationResult) return 0;

    let count = 0;
    for (const emp of validationResult.employee_results) {
      for (const issue of emp.issues) {
        if (issue.severity === 'WARNING') {
          if (!isAcknowledged(emp.employee_ni, issue.code)) {
            count++;
          }
        }
      }
    }

    return count;
  }, [validationResult, isAcknowledged]);

  return {
    isAcknowledged,
    acknowledge,
    acknowledgeAllForEmployee,
    acknowledgeAll,
    reset,
    allAcknowledged,
    unacknowledgedCount,
  };
}

// =============================================================================
// EXPORTS
// =============================================================================

export type {
  SubmissionValidationResult,
  ValidationSummaryResult,
  EmployeeValidationResult,
  NIValidationResult,
};
